import React, { useState, useMemo } from 'react';
import { SelectAnalysisExcelFile } from './components/SelectAnalysisExcelFile';
import { SheetDataViewer } from '../components/SheetDataViewer';
import { ReviewAnalysisStep } from './components/ReviewAnalysisStep';
import { SelectSupportingFile } from './components/SelectSupportingFile';
import { SendingToInventory } from '../components/SendingToInventory';
import { ImportHistoryPanel } from '../components/ImportHistoryPanel';
import { ALL_AWS_TRANSFORM_ANALYSIS_EXCEL_SHEETS, IMPORT_TYPES } from '../../../../constants/discovery';

import { Typography, LoaderAnimation, Form, Steps } from '../../../../components';

const { Paragraph, Title } = Typography;

import './AWSTransform.scss';

export const AWSTransform = () => {
    const [fileList, setFileList] = useState([]);
    const [supportingFileList, setSupportingFileList] = useState([]);
    const [step, setStep] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [activeImportId, setActiveImportId] = useState(null);
    const [resetCount, setResetCount] = useState(0);
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);

    const [form] = Form.useForm();

    const completeStep = () => {
        setStep(step + 1);
    };

    const resetForm = () => {
        setIsUploading(false);
        setFileList([]);
        setSupportingFileList([]);
        setStep(0);
        setResetCount((count) => count + 1);
    };

    const handleDeleteAnalysisFile = () => {
        setFileList([]);
        setStep(0);
    };

    const handleDeleteSupportingFile = () => {
        setSupportingFileList([]);
    };

    const stepItems = useMemo(
        () => [
            {
                title: 'Select File',
                content: step === 0 ? 'Select analysis.xlsx file' : undefined,
            },
            {
                title: 'Processing File',
            },
            {
                title: 'Review',
                content: step === 2 ? 'Review your data before proceeding' : undefined,
            },
            {
                title: 'Supporting File',
                content: step === 3 ? 'Upload RVTools or AWS JSON' : undefined,
            },
            {
                title: 'Adding to Inventory',
            },
        ],
        [step],
    );

    return (
        <div className="discovery-aws-transform">
            <div className="aws-transform-page-header">
                <Title level={2} className="aws-transform-page-title">
                    Import AWS Transform
                </Title>
                <Paragraph>
                    Upload your AWS Transform analysis file and supporting files (RVTools export or AWS Discovery Tool)
                    to populate your Inventory. We&apos;ll analyze your VMs and hosts to surface insights on capacity,
                    performance, and rightsizing potential.
                </Paragraph>
            </div>
            <div>
                <div className="csv-upload-dialog">
                    <div className="aws-transform-left-col-upload">
                        <Steps orientation="vertical" current={step} items={stepItems} />
                    </div>
                    <div className="aws-transform-right-col-upload">
                        {step === 0 && (
                            <SelectAnalysisExcelFile
                                key={resetCount}
                                resetForm={resetForm}
                                fileList={fileList}
                                setFileList={setFileList}
                                handleDelete={handleDeleteAnalysisFile}
                                completeStep={completeStep}
                                form={form}
                            />
                        )}
                        {step === 1 && <LoaderAnimation size="small" color="grayscale" />}
                        {step === 2 && <ReviewAnalysisStep resetForm={resetForm} completeStep={completeStep} />}
                        {step === 3 && (
                            <SelectSupportingFile
                                key={resetCount}
                                resetForm={resetForm}
                                form={form}
                                analysisFileList={fileList}
                                fileList={supportingFileList}
                                setFileList={setSupportingFileList}
                                handleDelete={handleDeleteSupportingFile}
                                orgId={orgId}
                                isUploading={isUploading}
                                setIsUploading={setIsUploading}
                                setStep={setStep}
                                setActiveImportId={setActiveImportId}
                            />
                        )}
                        {step === 4 && (
                            <SendingToInventory
                                setStep={setStep}
                                activeImportId={activeImportId}
                                sourceLabel="Your AWS Transform data"
                            />
                        )}
                    </div>
                </div>
                {[1, 2].includes(step) && !isUploading && (
                    <SheetDataViewer
                        file={fileList[0]?.originFileObj}
                        excelSheetsToDisplay={ALL_AWS_TRANSFORM_ANALYSIS_EXCEL_SHEETS}
                        setStep={setStep}
                    />
                )}
                {!isUploading && <ImportHistoryPanel orgId={orgId} importType={IMPORT_TYPES.awsTransform} />}
                {isUploading && <LoaderAnimation color="blueBackground" size="small" />}
            </div>
        </div>
    );
};
